import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { PluginListenerHandle } from "@capacitor/core";
import { isNativeApp, nativePlatform, deepLinkToPath, decideBackAction } from "@/lib/native";
import { supabase } from "@/integrations/supabase/client";

/**
 * Integrações nativas do app (no navegador, não faz nada):
 * - esconde a splash após o primeiro paint
 * - sincroniza fundo e indicadores da barra de status com a tela/tema
 * - botão Voltar do Android (fecha diálogo → volta → sai)
 * - universal links / app links → rota interna
 * - retomada do app: renova sessão
 * - aviso de falta de conexão
 */
export function NativeRuntime() {
  const navigate = useNavigate();
  const location = useLocation();
  const [offline, setOffline] = useState(false);
  const syncStatusRef = useRef<((force?: boolean) => void) | null>(null);

  // A rota muda depois da montagem do runtime; o tema também pode mudar sem navegação.
  useEffect(() => {
    syncStatusRef.current?.();
  }, [location.pathname]);

  useEffect(() => {
    if (!isNativeApp()) return;
    document.documentElement.classList.add("native-app", `native-${nativePlatform()}`);
    const handles: PluginListenerHandle[] = [];
    let cancelled = false;
    let requested: { color: string; dark: boolean; key: string; force: boolean } | null = null;
    let running = false;
    let inFlightKey: string | null = null;
    let lastAppliedKey: string | null = null;
    const register = (handle: PluginListenerHandle) => {
      if (cancelled) void handle.remove().catch(() => {});
      else handles.push(handle);
    };

    // Lê os tokens renderizados, sem impor outra paleta ao site. O header usa bg-card/80;
    // a cor sólida correspondente ao token --card evita uma faixa translúcida sobre o notch.
    const statusAppearance = () => {
      const header = document.querySelector("[data-native-status-header]");
      const token = header ? "--card" : "--background";
      const hsl = getComputedStyle(document.documentElement).getPropertyValue(token)
        .trim().match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/)?.slice(1).map(Number);
      if (!hsl || hsl.length !== 3) return null;
      const [hue, saturation, lightness] = hsl;
      const s = saturation / 100;
      const l = lightness / 100;
      const chroma = (1 - Math.abs(2 * l - 1)) * s;
      const x = chroma * (1 - Math.abs(((hue % 360) / 60) % 2 - 1));
      const pairs = [[chroma, x, 0], [x, chroma, 0], [0, chroma, x], [0, x, chroma], [x, 0, chroma], [chroma, 0, x]];
      const rgb = pairs[Math.floor(((hue % 360) + 360) % 360 / 60)].map((v) => (v + l - chroma / 2) * 255);
      const [red, green, blue] = rgb;
      const color = `#${rgb.map((channel) => Math.round(channel).toString(16).padStart(2, "0")).join("")}`;
      const dark = (red * 0.2126 + green * 0.7152 + blue * 0.0722) < 128;
      return { color, dark };
    };

    (async () => {
      const [{ App }, { SplashScreen }, { StatusBar, Style }, { Network }] = await Promise.all([
        import("@capacitor/app"),
        import("@capacitor/splash-screen"),
        import("@capacitor/status-bar"),
        import("@capacitor/network"),
      ]);
      if (cancelled) return;

      requestAnimationFrame(() => {
        SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
      });
      const flushStatus = async () => {
        if (running) return;
        running = true;
        try {
          while (!cancelled && requested) {
            const next = requested;
            requested = null;
            if (!next.force && next.key === lastAppliedKey) continue;
            inFlightKey = next.key;
            // Serializa as chamadas: uma resposta atrasada nunca vence a tela mais recente.
            const colorOk = await StatusBar.setBackgroundColor({ color: next.color }).then(() => true, () => false);
            if (cancelled) break;
            const styleOk = await StatusBar.setStyle({ style: next.dark ? Style.Dark : Style.Light }).then(() => true, () => false);
            lastAppliedKey = colorOk && styleOk ? next.key : null;
            inFlightKey = null;
          }
        } finally {
          inFlightKey = null;
          running = false;
        }
      };
      syncStatusRef.current = (force = false) => {
        if (cancelled) return;
        const next = statusAppearance();
        if (!next) return;
        const key = `${next.color}:${next.dark}`;
        if (!force && (requested?.key === key || (!requested && (inFlightKey === key || (!running && lastAppliedKey === key))))) return;
        requested = { ...next, key, force };
        void flushStatus();
      };
      // next-themes troca a classe na raiz; páginas protegidas montam o header depois.
      const observer = new MutationObserver(() => syncStatusRef.current?.());
      register({ remove: async () => observer.disconnect() });
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
      observer.observe(document.body, { childList: true, subtree: true });
      syncStatusRef.current();

      register(
        await App.addListener("backButton", ({ canGoBack }) => {
          const overlayOpen = !!document.querySelector(
            '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-radix-popper-content-wrapper]',
          );
          const action = decideBackAction({ overlayOpen, canGoBack: canGoBack || window.history.length > 1 });
          if (action === "closeOverlay") {
            document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
          } else if (action === "goBack") {
            navigate(-1);
          } else {
            App.exitApp().catch(() => {});
          }
        }),
      );

      register(
        await App.addListener("appUrlOpen", ({ url }) => {
          const path = deepLinkToPath(url);
          if (path) navigate(path);
        }),
      );

      register(
        await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) {
            syncStatusRef.current?.(true);
            // Renova o token se estiver perto de expirar; sessão inválida → onAuthStateChange desloga
            supabase.auth.getSession().catch(() => {});
          }
        }),
      );

      const status = await Network.getStatus().catch(() => null);
      if (status && !cancelled) setOffline(!status.connected);
      register(await Network.addListener("networkStatusChange", (s) => { if (!cancelled) setOffline(!s.connected); }));
    })().catch(() => {});

    return () => {
      cancelled = true;
      syncStatusRef.current = null;
      handles.forEach((h) => h.remove().catch(() => {}));
    };
  }, [navigate]);

  if (!offline) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[100] bg-destructive text-destructive-foreground text-center text-sm py-2"
      style={{ paddingTop: "max(env(safe-area-inset-top), 0.5rem)" }}
    >
      Sem conexão com a internet. Algumas informações podem não carregar.
    </div>
  );
}
